import React, { useState } from 'react';
import { Upload, UploadProps, Modal, Input, Button as AntdButton, Space, Tooltip } from 'antd';
import {
  EyeOutlined,
  DownloadOutlined,
  DeleteOutlined,
  UpOutlined,
  DownOutlined,
  HolderOutlined,
  PictureOutlined,
  VideoCameraOutlined,
  FilePdfOutlined,
  FileOutlined,
} from '@ant-design/icons';
import { UploadFile } from 'antd/es/upload';
import { useUploaderLogic, getMediaType, downloadFile, MediaType } from './logic/useUploader.logic';
import { addLinkButtonContainerStyle, modalInputStyle, errorTextStyle } from './Uploader.style';
import { Button } from '../Element';

export interface UploaderProps extends Omit<UploadProps, 'fileList' | 'value' | 'onChange'> {
  value?: UploadFile[];
  onChange?: (files: UploadFile[]) => void;
  fileList?: UploadFile[];
  maxSize?: number;
  accept?: string;
  isDragger?: boolean;
  buttonText?: string;
  description?: string;
  showUploadList?: boolean;
  onFileChange?: (files: UploadFile[]) => void;
  onPreviewFileChange?: (previewFiles: any[]) => void;
  customUpload?: boolean;
  customRequest?: (options: any) => void;
  action?: string;
}

const Uploader: React.FC<UploaderProps> = ({
  value,
  onChange,
  fileList: propFileList,
  maxSize = 5, // Default max size in MB
  accept,
  isDragger = false,
  buttonText = 'Upload',
  description = 'Drag & drop files here or click to browse',
  showUploadList = true,
  onFileChange,
  onPreviewFileChange,
  customUpload = true,
  customRequest,
  action,
  ...props
}) => {
  const {
    files,
    beforeUpload,
    handleCustomRequest,
    handleChange,
    addLinkFile,
    reorderFilesByUid,
    moveFileByUid,
    removeFileByUid,
    getObjectUrlForUid,
  } = useUploaderLogic({
    value,
    onChange,
    fileList: propFileList,
    maxSize,
    onFileChange,
    onPreviewFileChange,
    customUpload,
    customRequest,
    action,
  });

  // State for link input
  const [showLinkModal, setShowLinkModal] = useState(false);
  const [linkValue, setLinkValue] = useState('');
  const [linkLoading, setLinkLoading] = useState(false);
  const [linkError, setLinkError] = useState('');

  // State for media preview modal
  const [previewFile, setPreviewFile] = useState<UploadFile | null>(null);
  const [previewType, setPreviewType] = useState<MediaType>('document');
  const [draggedUid, setDraggedUid] = useState<string | null>(null);

  // Add file from link
  const handleAddLink = () => {
    setLinkError('');
    if (!linkValue) {
      setLinkError('Please enter a link.');
      return;
    }
    try {
      setLinkLoading(true);
      const url = linkValue.trim();
      addLinkFile(url);
      setShowLinkModal(false);
      setLinkValue('');
    } catch (e) {
      setLinkError('Invalid URL provided. Please enter a valid URL.');
    } finally {
      setLinkLoading(false);
    }
  };

  const handlePreview = (file: UploadFile) => {
    const type = getMediaType(file);
    const objectUrl = getObjectUrlForUid(file.uid);
    const url = objectUrl || (file as any).thumbUrl || file.url || (file as any).path;

    if (type === 'pdf' || type === 'document') {
      if (url) {
        const win = window.open(url, '_blank', 'noopener,noreferrer');
        if (win) win.opener = null;
      }
      return;
    }

    setPreviewType(type);
    setPreviewFile(file);
  };

  const renderFileIcon = (file: UploadFile) => {
    const type = getMediaType(file);
    switch (type) {
      case 'image':
        return <PictureOutlined style={{ color: '#1890ff', fontSize: '18px' }} />;
      case 'video':
        return <VideoCameraOutlined style={{ color: '#fa8c16', fontSize: '18px' }} />;
      case 'pdf':
        return <FilePdfOutlined style={{ color: '#ff4d4f', fontSize: '18px' }} />;
      default:
        return <FileOutlined style={{ color: '#52c41a', fontSize: '18px' }} />;
    }
  };

  const uploadProps: UploadProps = {
    name: 'file',
    beforeUpload,
    fileList: typeof files === 'object' ? files || [] : [],
    onChange: handleChange,
    showUploadList: false, // We render our custom drag/reorder/preview list below
    accept,
    ...(customUpload ? { customRequest: handleCustomRequest } : { action }),
    ...props,
  };

  const previewUrl = previewFile
    ? getObjectUrlForUid(previewFile.uid) || (previewFile as any).thumbUrl || previewFile.url || (previewFile as any).path
    : '';

  return (
    <>
      <Upload.Dragger {...uploadProps}>
        <div className="flex flex-col items-center p-4 w-full rounded-md border-2 border-dashed border-gray-300">
          <div className="font-medium">{buttonText}</div>
          <p className="text-gray-500 text-sm mt-2">{description}</p>
        </div>
      </Upload.Dragger>

      <div style={addLinkButtonContainerStyle}>
        <Button title="Add Link" onClick={() => setShowLinkModal(true)}>
          Add Link
        </Button>
      </div>

      {showUploadList && Array.isArray(files) && files.length > 0 && (
        <div style={{ marginTop: '12px', display: 'flex', flexDirection: 'column', gap: '8px' }}>
          {files.map((file, index) => {
            const uid = file.uid || (file as any).id;
            return (
              <div
                key={uid || index}
                draggable={true}
                onDragStart={(e) => {
                  setDraggedUid(uid);
                  e.dataTransfer.setData('text/plain', uid);
                }}
                onDragOver={(e) => {
                  e.preventDefault();
                }}
                onDrop={(e) => {
                  e.preventDefault();
                  const fromUid = e.dataTransfer.getData('text/plain') || draggedUid;
                  if (fromUid && fromUid !== uid) {
                    reorderFilesByUid(fromUid, uid);
                  }
                  setDraggedUid(null);
                }}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  padding: '8px 12px',
                  border: '1px solid #d9d9d9',
                  borderRadius: '6px',
                  backgroundColor: draggedUid === uid ? '#f0f5ff' : '#fff',
                  cursor: 'grab',
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', overflow: 'hidden', flex: 1 }}>
                  <HolderOutlined style={{ color: '#8c8c8c', cursor: 'grab' }} title="Drag to reorder" />
                  {renderFileIcon(file)}
                  <span
                    style={{
                      fontSize: '13px',
                      fontWeight: 500,
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      maxWidth: '220px',
                    }}
                    title={file.name}
                  >
                    {file.name}
                  </span>
                </div>

                <Space size="small">
                  <Tooltip title="Preview">
                    <AntdButton
                      type="text"
                      size="small"
                      aria-label="Preview"
                      icon={<EyeOutlined />}
                      onClick={() => handlePreview(file)}
                    />
                  </Tooltip>

                  <Tooltip title="Download">
                    <AntdButton
                      type="text"
                      size="small"
                      aria-label="Download"
                      icon={<DownloadOutlined />}
                      onClick={() => downloadFile(file, getObjectUrlForUid(file.uid))}
                    />
                  </Tooltip>

                  <Tooltip title="Move Up">
                    <AntdButton
                      type="text"
                      size="small"
                      aria-label="Move Up"
                      icon={<UpOutlined />}
                      disabled={index === 0}
                      onClick={() => moveFileByUid(uid, 'up')}
                    />
                  </Tooltip>

                  <Tooltip title="Move Down">
                    <AntdButton
                      type="text"
                      size="small"
                      aria-label="Move Down"
                      icon={<DownOutlined />}
                      disabled={index === files.length - 1}
                      onClick={() => moveFileByUid(uid, 'down')}
                    />
                  </Tooltip>

                  <Tooltip title="Remove">
                    <AntdButton
                      type="text"
                      size="small"
                      danger
                      aria-label="Remove"
                      icon={<DeleteOutlined />}
                      onClick={() => removeFileByUid(uid)}
                    />
                  </Tooltip>
                </Space>
              </div>
            );
          })}
        </div>
      )}

      <Modal
        title="Add Link"
        open={showLinkModal}
        onOk={handleAddLink}
        onCancel={() => {
          setShowLinkModal(false);
          setLinkError('');
        }}
        confirmLoading={linkLoading}
        okText="Add Link"
        cancelText="Cancel"
      >
        <Input
          placeholder="Add file link here..."
          value={linkValue}
          onChange={(e) => setLinkValue(e.target.value)}
          onPressEnter={handleAddLink}
          disabled={linkLoading}
          style={modalInputStyle}
        />
        {linkError && <div style={errorTextStyle}>{linkError}</div>}
      </Modal>

      <Modal
        title={previewFile?.name || 'Media Preview'}
        open={!!previewFile}
        footer={[
          <AntdButton
            key="download"
            icon={<DownloadOutlined />}
            onClick={() => previewFile && downloadFile(previewFile, getObjectUrlForUid(previewFile.uid))}
          >
            Download
          </AntdButton>,
          <AntdButton key="close" type="primary" onClick={() => setPreviewFile(null)}>
            Close
          </AntdButton>,
        ]}
        onCancel={() => setPreviewFile(null)}
        width={720}
        destroyOnClose
      >
        {previewType === 'image' && (
          <div style={{ display: 'flex', justifyContent: 'center', padding: '12px' }}>
            <img
              src={previewUrl}
              alt={previewFile?.name || 'Preview'}
              style={{ maxWidth: '100%', maxHeight: '65vh', objectFit: 'contain', borderRadius: '4px' }}
            />
          </div>
        )}

        {previewType === 'video' && (
          <div style={{ display: 'flex', justifyContent: 'center', padding: '12px' }}>
            <video src={previewUrl} controls autoPlay style={{ width: '100%', maxHeight: '65vh', borderRadius: '4px' }} />
          </div>
        )}
      </Modal>
    </>
  );
};

export default Uploader;
